import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, ShieldCheck, LogOut, Check, X, Podcast, Quote, Images, Send, Upload, Pencil, ChevronUp, ChevronDown, ArrowDownUp, Eye, Users, CalendarDays, TrendingUp, Globe2, MapPin } from "lucide-react";
import AudioPlayer from "@/components/AudioPlayer";
import AudioRecorder from "@/components/AudioRecorder";
import { RELATIONS } from "@/lib/relations";
import { DEFAULT_CHAPTERS } from "@/lib/story";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })} à ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
};

const timeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  return `il y a ${Math.floor(h / 24)} j`;
};

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("memorium-admin-token"));
  const [form, setForm] = useState({ email: "", password: "" });
  const [messages, setMessages] = useState([]);
  const [tab, setTab] = useState("pending");
  const [busy, setBusy] = useState(false);
  const [life, setLife] = useState({ fr: null, tori: null, goun: null });
  const [lifeFiles, setLifeFiles] = useState({ fr: null, tori: null });
  const [trib, setTrib] = useState({ name: "", relation: "", message: "" });
  const [tribOther, setTribOther] = useState("");
  const [tribKind, setTribKind] = useState("text");
  const [tribLang, setTribLang] = useState("Tori");
  const [tribFile, setTribFile] = useState(null);
  const [tribBlob, setTribBlob] = useState(null);
  const [tribRecKey, setTribRecKey] = useState(0);
  const [tribBusy, setTribBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", relation: "", message: "", lang: "Tori" });
  const [editOther, setEditOther] = useState("");
  const [story, setStory] = useState(DEFAULT_CHAPTERS);
  const [storyBusy, setStoryBusy] = useState(false);
  const [orderOverride, setOrderOverride] = useState(null);
  const [orderDirty, setOrderDirty] = useState(false);
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setOrderOverride(null);
    setOrderDirty(false);
    try {
      const r = await axios.get(`${API}/admin/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(r.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("memorium-admin-token");
        setToken(null);
      }
    }
    axios.get(`${API}/life-audio`).then((r) => setLife(r.data)).catch(() => {});
    axios
      .get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStats(r.data))
      .catch(() => {});
    axios
      .get(`${API}/story`)
      .then((r) => {
        if (r.data.chapters) setStory(r.data.chapters);
      })
      .catch(() => {});
  }, [token]);

  const uploadLife = async (langKey) => {
    const file = lifeFiles[langKey];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("lang", langKey);
      fd.append("file", file);
      await axios.post(`${API}/admin/life-audio`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLifeFiles({ ...lifeFiles, [langKey]: null });
      toast.success(`Audio ${langKey.toUpperCase()} du parcours de vie déposé.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Dépôt impossible.");
    }
  };

  const addTribute = async (e) => {
    e.preventDefault();
    if (!trib.name.trim()) {
      toast.error("Indiquez le nom de la personne.");
      return;
    }
    if (tribKind === "text" && !trib.message.trim()) {
      toast.error("Écrivez le message de l'hommage.");
      return;
    }
    if (tribKind === "audio" && !tribBlob && !tribFile) {
      toast.error("Enregistrez ou choisissez un fichier audio.");
      return;
    }
    setTribBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", trib.name.trim());
      fd.append(
        "relation",
        trib.relation === "Toute autre personne" && tribOther.trim()
          ? tribOther.trim()
          : trib.relation
      );
      fd.append("kind", tribKind);
      if (tribKind === "text") {
        fd.append("message", trib.message.trim());
      } else {
        fd.append("lang", tribLang);
        if (tribBlob) fd.append("audio", tribBlob, "hommage-vocal.webm");
        else fd.append("audio", tribFile);
      }
      await axios.post(`${API}/admin/messages`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTrib({ name: "", relation: "", message: "" });
      setTribOther("");
      setTribFile(null);
      setTribBlob(null);
      setTribRecKey((k) => k + 1);
      setTab("approved");
      toast.success("Hommage ajouté et publié sur le site.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ajout impossible.");
    } finally {
      setTribBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/login`, form);
      localStorage.setItem("memorium-admin-token", r.data.token);
      setToken(r.data.token);
      toast.success("Bienvenue dans l'espace famille.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connexion impossible.");
    } finally {
      setBusy(false);
    }
  };

  const act = async (id, action) => {
    try {
      if (action === "approve") {
        await axios.post(`${API}/admin/messages/${id}/approve`, null, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Hommage publié sur le site.");
      } else {
        await axios.delete(`${API}/admin/messages/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Hommage refusé et supprimé.");
      }
      load();
    } catch {
      toast.error("Action impossible.");
    }
  };

  const startEdit = (m) => {
    const known = RELATIONS.includes(m.relation);
    setEditing(m.id);
    setEditForm({
      name: m.name,
      relation: known ? m.relation : m.relation ? "Toute autre personne" : "",
      message: m.message || "",
      lang: m.lang || "Tori",
    });
    setEditOther(!known && m.relation ? m.relation : "");
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) {
      toast.error("Le nom est requis.");
      return;
    }
    try {
      await axios.put(
        `${API}/admin/messages/${editing}`,
        {
          name: editForm.name.trim(),
          relation:
            (editForm.relation === "Toute autre personne" && editOther.trim()
              ? editOther.trim()
              : editForm.relation) || null,
          message: editForm.message,
          lang: editForm.lang,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditing(null);
      toast.success("Hommage modifié.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Modification impossible.");
    }
  };

  const saveStory = async () => {
    setStoryBusy(true);
    try {
      await axios.put(
        `${API}/admin/story`,
        { chapters: story },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("L'Histoire du Patriarche a été mise à jour.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Enregistrement impossible.");
    } finally {
      setStoryBusy(false);
    }
  };

  const updateChapter = (idx, fieldKey, value) => {
    setStory((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, [fieldKey]: value } : c))
    );
  };

  const logout = () => {
    localStorage.removeItem("memorium-admin-token");
    setToken(null);
  };

  const field =
    "w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-800 placeholder:text-stone-400 focus:border-[#C5A059] focus:outline-none focus:ring-2 focus:ring-[#C5A059]/30";

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF8F5] px-4">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm rounded-3xl border border-stone-200/80 bg-white p-8 shadow-sm"
        >
          <div className="mb-6 text-center">
            <ShieldCheck className="mx-auto h-8 w-8 text-[#C5A059]" />
            <h1 className="mt-3 font-display text-2xl font-semibold text-stone-900">
              Espace Famille
            </h1>
            <p className="mt-1 text-xs text-stone-500">
              Validation des hommages — Mémorium
            </p>
          </div>
          <div className="space-y-4">
            <input
              data-testid="admin-email-input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Adresse e-mail"
              className={field}
            />
            <input
              data-testid="admin-password-input"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Mot de passe"
              className={field}
            />
            <button
              data-testid="admin-login-button"
              type="submit"
              disabled={busy}
              className="w-full rounded-full bg-[#1C1917] px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#FAF8F5] transition-colors duration-300 hover:bg-[#C85A32] disabled:opacity-60"
            >
              {busy ? "Connexion…" : "Se connecter"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  const pending = messages.filter((m) => m.status === "pending");
  const approved =
    orderOverride ?? messages.filter((m) => m.status === "approved");
  const shown = tab === "pending" ? pending : approved;

  const moveTribute = (id, dir) => {
    const list = [...approved];
    const i = list.findIndex((m) => m.id === id);
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    setOrderOverride(list);
    setOrderDirty(true);
  };

  const saveOrder = async () => {
    try {
      await axios.post(
        `${API}/admin/messages/reorder`,
        { ids: approved.map((m) => m.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrderDirty(false);
      setOrderOverride(null);
      toast.success("Ordre des hommages enregistré sur le site.");
      load();
    } catch {
      toast.error("Enregistrement impossible.");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF8F5] px-4 py-10">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-semibold text-stone-900">
              Espace Famille
            </h1>
            <p className="mt-1 text-sm text-stone-500">
              {pending.length} hommage{pending.length > 1 ? "s" : ""} en attente de
              validation • {approved.length} publié{approved.length > 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              className="rounded-full border border-stone-300 px-4 py-2 text-xs font-semibold text-stone-600 transition-colors hover:border-[#C5A059]"
            >
              Voir le site
            </a>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-xs font-semibold text-stone-600 transition-colors hover:border-[#C85A32] hover:text-[#C85A32]"
            >
              <LogOut className="h-3.5 w-3.5" /> Déconnexion
            </button>
          </div>
        </div>

        {stats && (
          <div data-testid="admin-stats" className="mt-8 rounded-2xl border border-stone-200/80 bg-white p-5 shadow-sm">
            <h2 className="font-display text-lg font-semibold text-stone-900">
              Statistiques des visites
            </h2>
            <p className="mt-1 text-xs text-stone-500">
              Qui visite le mémorial, d'où et quand. Les adresses IP sont
              anonymisées, aucune donnée personnelle n'est conservée.
            </p>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { Icon: Eye, label: "Visites totales", value: stats.total, tid: "stats-total" },
                { Icon: Users, label: "Visiteurs uniques", value: stats.unique_visitors, tid: "stats-unique" },
                { Icon: CalendarDays, label: "Aujourd'hui", value: stats.today.visits, tid: "stats-today" },
                { Icon: TrendingUp, label: "7 derniers jours", value: stats.week.visits, tid: "stats-week" },
              ].map((c) => (
                <div key={c.tid} data-testid={c.tid} className="rounded-xl border border-stone-200 bg-[#F4EFEA]/60 p-4">
                  <c.Icon className="h-4 w-4 text-[#C5A059]" />
                  <p className="mt-2 font-display text-2xl font-semibold text-stone-900">{c.value}</p>
                  <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-stone-400">{c.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-400">
                Visites des 14 derniers jours
              </p>
              <div data-testid="stats-daily-chart" className="mt-2 flex h-24 items-end gap-1">
                {stats.daily.map((d) => (
                  <div key={d.date} className="group relative flex-1">
                    <div
                      className="w-full rounded-t-sm bg-[#C5A059]/70 transition-colors duration-200 group-hover:bg-[#C85A32]"
                      style={{
                        height: `${Math.max(3, Math.round((d.visits / Math.max(1, ...stats.daily.map((x) => x.visits))) * 88))}px`,
                      }}
                    />
                    <div className="pointer-events-none absolute -top-9 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-lg bg-[#1C1917] px-2 py-1 text-[10px] text-[#FAF8F5] opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                      {new Date(`${d.date}T12:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} — {d.visits} visite{d.visits > 1 ? "s" : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <div data-testid="stats-countries">
                <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-400">
                  <Globe2 className="h-3.5 w-3.5 text-[#C5A059]" /> Pays d'origine
                </p>
                {stats.countries.length === 0 ? (
                  <p className="mt-3 text-xs text-stone-400">Aucune donnée de localisation pour le moment.</p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {stats.countries.map((c) => (
                      <li key={c.country} className="text-xs">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-stone-700">{c.country}</span>
                          <span className="text-stone-400">
                            {c.visits} visite{c.visits > 1 ? "s" : ""} • {c.visitors} visiteur{c.visitors > 1 ? "s" : ""}
                          </span>
                        </div>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-stone-100">
                          <div
                            className="h-full rounded-full bg-[#C5A059]"
                            style={{ width: `${Math.round((c.visits / stats.countries[0].visits) * 100)}%` }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div data-testid="stats-cities">
                <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-400">
                  <MapPin className="h-3.5 w-3.5 text-[#C5A059]" /> Villes
                </p>
                {stats.cities.length === 0 ? (
                  <p className="mt-3 text-xs text-stone-400">Aucune ville identifiée pour le moment.</p>
                ) : (
                  <ul className="mt-3 space-y-1.5">
                    {stats.cities.map((c) => (
                      <li key={`${c.city}-${c.country}`} className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-stone-700">
                          {c.city} <span className="font-normal text-stone-400">({c.country})</span>
                        </span>
                        <span className="text-stone-400">{c.visits}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="mt-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-400">
                Visites récentes
              </p>
              {stats.recent.length === 0 ? (
                <p className="mt-3 text-xs text-stone-400">Aucune visite enregistrée pour le moment.</p>
              ) : (
                <ul data-testid="stats-recent" className="mt-3 divide-y divide-stone-100">
                  {stats.recent.map((v, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 py-2 text-xs">
                      <span className="flex items-center gap-2 text-stone-600">
                        <MapPin className="h-3 w-3 shrink-0 text-[#C5A059]" />
                        {v.city ? `${v.city}, ` : ""}{v.country || "Localisation inconnue"}
                        <span className="text-stone-400">— {v.path === "/" ? "Accueil" : v.path}</span>
                      </span>
                      <span className="shrink-0 text-stone-400">{timeAgo(v.at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <div className="mt-8 rounded-2xl border border-stone-200/80 bg-white p-5 shadow-sm">
          <h2 className="font-display text-lg font-semibold text-stone-900">
            Parcours de vie — audios
          </h2>
          <p className="mt-1 text-xs text-stone-500">
            Ces lectures audio apparaissent dans la section Biographie du site.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {[
              { key: "fr", label: "Français" },
              { key: "tori", label: "Tori" },
              { key: "goun", label: "Goun" },
            ].map((l) => (
              <div key={l.key} className="rounded-xl border border-stone-200 p-4">
                <p className="flex items-center justify-between text-sm font-semibold text-stone-800">
                  Version {l.label}
                  <span
                    data-testid={`life-audio-status-${l.key}`}
                    className={`text-[10px] font-semibold uppercase tracking-wide ${
                      life?.[l.key] ? "text-[#C5A059]" : "text-stone-400"
                    }`}
                  >
                    {life?.[l.key] ? "Déposé" : "À déposer"}
                  </span>
                </p>
                {life?.[l.key] && (
                  <div className="mt-2">
                    <AudioPlayer
                      src={`${API}/files/${life[l.key]}`}
                      testId={`admin-life-audio-player-${l.key}`}
                    />
                  </div>
                )}
                <input
                  data-testid={`admin-life-audio-${l.key}-input`}
                  type="file"
                  accept="audio/*"
                  onChange={(e) =>
                    setLifeFiles({ ...lifeFiles, [l.key]: e.target.files?.[0] || null })
                  }
                  className="mt-3 w-full text-xs text-stone-500"
                />
                <button
                  data-testid={`admin-life-audio-${l.key}-upload`}
                  onClick={() => uploadLife(l.key)}
                  disabled={!lifeFiles[l.key]}
                  className="mt-2 w-full rounded-full bg-[#1C1917] px-4 py-2 text-xs font-semibold text-[#FAF8F5] transition-colors hover:bg-[#C5A059] hover:text-[#1C1917] disabled:opacity-40"
                >
                  Déposer l'audio
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 rounded-2xl border border-stone-200/80 bg-white p-5 shadow-sm">
          <h2 className="font-display text-lg font-semibold text-stone-900">
            L'Histoire du Patriarche
          </h2>
          <p className="mt-1 text-xs text-stone-500">
            Modifiez les chapitres affichés sur le site. Séparez les
            paragraphes par une ligne vide.
          </p>
          <div className="mt-4 space-y-5">
            {story.map((ch, idx) => (
              <div
                key={ch.num}
                data-testid={`admin-story-chapter-${idx + 1}`}
                className="rounded-xl border border-stone-200 p-4"
              >
                <div className="grid gap-3 sm:grid-cols-[64px_1fr_1fr]">
                  <input
                    data-testid={`admin-story-num-${idx + 1}`}
                    value={ch.num}
                    onChange={(e) => updateChapter(idx, "num", e.target.value)}
                    maxLength={6}
                    className={`${field} text-center font-display font-semibold`}
                    placeholder="N°"
                  />
                  <input
                    data-testid={`admin-story-title-${idx + 1}`}
                    value={ch.title}
                    onChange={(e) => updateChapter(idx, "title", e.target.value)}
                    maxLength={120}
                    className={field}
                    placeholder="Titre du chapitre"
                  />
                  <input
                    data-testid={`admin-story-era-${idx + 1}`}
                    value={ch.era}
                    onChange={(e) => updateChapter(idx, "era", e.target.value)}
                    maxLength={120}
                    className={field}
                    placeholder="Époque (ex. 1938 — Banigbé-Gare)"
                  />
                </div>
                <textarea
                  data-testid={`admin-story-intro-${idx + 1}`}
                  value={ch.intro}
                  onChange={(e) => updateChapter(idx, "intro", e.target.value)}
                  rows={3}
                  maxLength={1200}
                  className={`${field} mt-3 resize-none`}
                  placeholder="Premier paragraphe (toujours visible)"
                />
                <textarea
                  data-testid={`admin-story-rest-${idx + 1}`}
                  value={(ch.rest || []).join("\n\n")}
                  onChange={(e) =>
                    updateChapter(
                      idx,
                      "rest",
                      e.target.value.split(/\n\s*\n/).filter((p) => p.trim())
                    )
                  }
                  rows={5}
                  className={`${field} mt-3 resize-none`}
                  placeholder="Paragraphes dépliables (une ligne vide entre chaque)"
                />
              </div>
            ))}
          </div>
          <button
            data-testid="admin-story-save"
            onClick={saveStory}
            disabled={storyBusy}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#1C1917] px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#FAF8F5] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#1C1917] disabled:opacity-60"
          >
            <Send className="h-4 w-4" />
            {storyBusy ? "Enregistrement…" : "Enregistrer l'histoire"}
          </button>
        </div>

        <a
          href="/admin/medias"
          data-testid="admin-open-media-manager"
          className="group mt-8 flex items-center justify-between gap-4 rounded-2xl border border-stone-200/80 bg-white p-5 shadow-sm transition-colors duration-300 hover:border-[#C5A059]"
        >
          <div className="flex items-center gap-4">
            <span className="rounded-full bg-[#C5A059]/15 p-3 text-[#8a6b2e]">
              <Images className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-display text-lg font-semibold text-stone-900">Photos & vidéos</h2>
              <p className="mt-0.5 text-xs text-stone-500">
                Gérer l'album : dépôt multiple, thématiques, légendes, épingles et récupération des fichiers.
              </p>
            </div>
          </div>
          <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#8a6b2e] transition-transform duration-300 group-hover:translate-x-1">
            Ouvrir →
          </span>
        </a>

        <a
          href="/admin/documents"
          data-testid="admin-open-documents-manager"
          className="group mt-4 flex items-center justify-between gap-4 rounded-2xl border border-stone-200/80 bg-white p-5 shadow-sm transition-colors duration-300 hover:border-[#C5A059]"
        >
          <div className="flex items-center gap-4">
            <span className="rounded-full bg-[#C5A059]/15 p-3 text-[#8a6b2e]">
              <FileText className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-display text-lg font-semibold text-stone-900">Documents PDF</h2>
              <p className="mt-0.5 text-xs text-stone-500">
                Publier biographie, hommages, programmes… lisibles sur la page Documents du site.
              </p>
            </div>
          </div>
          <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#8a6b2e] transition-transform duration-300 group-hover:translate-x-1">
            Ouvrir →
          </span>
        </a>

        <div className="mt-8 rounded-2xl border border-stone-200/80 bg-white p-5 shadow-sm">
          <h2 className="font-display text-lg font-semibold text-stone-900">
            Ajouter un hommage
          </h2>
          <p className="mt-1 text-xs text-stone-500">
            Publié immédiatement sur le site, sans passage par la validation.
          </p>
          <form data-testid="admin-tribute-form" onSubmit={addTribute} className="mt-4 space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                data-testid="admin-tribute-name"
                value={trib.name}
                onChange={(e) => setTrib({ ...trib, name: e.target.value })}
                placeholder="Nom / Prénom de la personne"
                maxLength={120}
                className={field}
              />
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-400">
                  Je suis pour le défunt
                </label>
                <select
                  data-testid="admin-tribute-relation"
                  value={trib.relation}
                  onChange={(e) => setTrib({ ...trib, relation: e.target.value })}
                  className={field}
                >
                  <option value="">Choisir…</option>
                  {RELATIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                {trib.relation === "Toute autre personne" && (
                  <input
                    data-testid="admin-tribute-relation-other"
                    value={tribOther}
                    onChange={(e) => setTribOther(e.target.value)}
                    placeholder="Précisez la relation (ex. parrain, voisin…)"
                    maxLength={120}
                    className={`${field} mt-3`}
                  />
                )}
              </div>
            </div>
            <div className="flex rounded-full border border-stone-200 bg-[#F4EFEA] p-1">
              <button
                type="button"
                data-testid="admin-tribute-kind-text"
                onClick={() => setTribKind("text")}
                className={`flex-1 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-300 ${
                  tribKind === "text"
                    ? "bg-[#1C1917] text-[#FAF8F5]"
                    : "text-stone-500 hover:text-stone-800"
                }`}
              >
                Texte
              </button>
              <button
                type="button"
                data-testid="admin-tribute-kind-audio"
                onClick={() => setTribKind("audio")}
                className={`flex-1 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-300 ${
                  tribKind === "audio"
                    ? "bg-[#1C1917] text-[#FAF8F5]"
                    : "text-stone-500 hover:text-stone-800"
                }`}
              >
                Audio
              </button>
            </div>
            {tribKind === "text" ? (
              <div>
                <textarea
                  data-testid="admin-tribute-message"
                  value={trib.message}
                  onChange={(e) => setTrib({ ...trib, message: e.target.value })}
                  placeholder="Message de l'hommage"
                  rows={3}
                  maxLength={300}
                  className={`${field} resize-none`}
                />
                <p className="mt-1 text-right text-[11px] text-stone-400">
                  {trib.message.length}/300 caractères
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <select
                  data-testid="admin-tribute-lang"
                  value={tribLang}
                  onChange={(e) => setTribLang(e.target.value)}
                  className={field}
                >
                  <option value="Tori">Langue de l'audio : Tori</option>
                  <option value="Goun">Langue de l'audio : Goun</option>
                  <option value="Français">Langue de l'audio : Français</option>
                </select>
                <AudioRecorder key={tribRecKey} onChange={setTribBlob} />
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-stone-300 bg-white px-4 py-4 text-sm text-stone-500 transition-colors hover:border-[#C5A059]">
                  <Upload className="h-4 w-4 text-[#C5A059]" />
                  {tribFile ? tribFile.name : "ou choisir un fichier audio (15 Mo max)"}
                  <input
                    data-testid="admin-tribute-file"
                    type="file"
                    accept="audio/*"
                    className="hidden"
                    onChange={(e) => setTribFile(e.target.files?.[0] || null)}
                  />
                </label>
              </div>
            )}
            <button
              data-testid="admin-tribute-submit"
              type="submit"
              disabled={tribBusy}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#1C1917] px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#FAF8F5] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#1C1917] disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {tribBusy ? "Publication…" : "Ajouter et publier"}
            </button>
          </form>
        </div>

        <div className="mt-8 flex gap-2">
          <button
            data-testid="admin-tab-pending"
            onClick={() => setTab("pending")}
            className={`rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-300 ${
              tab === "pending"
                ? "bg-[#1C1917] text-[#FAF8F5]"
                : "border border-stone-300 text-stone-500 hover:border-[#C5A059]"
            }`}
          >
            En attente ({pending.length})
          </button>
          <button
            data-testid="admin-tab-approved"
            onClick={() => setTab("approved")}
            className={`rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-300 ${
              tab === "approved"
                ? "bg-[#1C1917] text-[#FAF8F5]"
                : "border border-stone-300 text-stone-500 hover:border-[#C5A059]"
            }`}
          >
            Publiés ({approved.length})
          </button>
        </div>

        {tab === "approved" && approved.length > 1 && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#C5A059]/30 bg-[#C5A059]/10 px-4 py-3">
            <p className="flex items-center gap-2 text-xs text-stone-600">
              <ArrowDownUp className="h-4 w-4 text-[#8a6b2e]" />
              Rangez les hommages avec les flèches ↑ ↓ puis enregistrez l'ordre.
            </p>
            {orderDirty && (
              <button
                data-testid="save-order-button"
                onClick={saveOrder}
                className="rounded-full bg-[#1C1917] px-5 py-2 text-xs font-semibold text-[#FAF8F5] transition-colors hover:bg-[#C5A059] hover:text-[#1C1917]"
              >
                Enregistrer l'ordre
              </button>
            )}
          </div>
        )}

        <div className="mt-6 space-y-4">
          {shown.length === 0 && (
            <p className="rounded-2xl border border-dashed border-stone-300 p-8 text-center text-sm text-stone-400">
              {tab === "pending"
                ? "Aucun hommage en attente."
                : "Aucun hommage publié pour le moment."}
            </p>
          )}
          {shown.map((m) => (
            <article
              key={m.id}
              data-testid={`admin-message-${m.id}`}
              className="rounded-2xl border border-stone-200/80 bg-white p-5 shadow-sm"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1.5 rounded-full bg-[#C5A059]/15 px-3 py-1 text-[11px] font-semibold text-[#8a6b2e]">
                  {m.kind === "audio" ? (
                    <Podcast className="h-3.5 w-3.5" />
                  ) : (
                    <Quote className="h-3.5 w-3.5" />
                  )}
                  {m.kind === "audio" ? `Audio • ${m.lang}` : "Texte"}
                </span>
                <span className="text-[11px] text-stone-400">
                  {formatDate(m.created_at)}
                </span>
              </div>
              {editing === m.id ? (
                <div className="mt-3 space-y-3 rounded-xl border border-[#C5A059]/40 bg-[#F4EFEA] p-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <input
                      data-testid={`edit-name-${m.id}`}
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      placeholder="Nom / Prénom"
                      maxLength={120}
                      className={field}
                    />
                    <select
                      data-testid={`edit-relation-${m.id}`}
                      value={editForm.relation}
                      onChange={(e) => setEditForm({ ...editForm, relation: e.target.value })}
                      className={field}
                    >
                      <option value="">Je suis pour le défunt — Choisir…</option>
                      {RELATIONS.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                  {editForm.relation === "Toute autre personne" && (
                    <input
                      data-testid={`edit-relation-other-${m.id}`}
                      value={editOther}
                      onChange={(e) => setEditOther(e.target.value)}
                      placeholder="Précisez la relation (ex. parrain, voisin…)"
                      maxLength={120}
                      className={field}
                    />
                  )}
                  {m.kind === "audio" ? (
                    <select
                      data-testid={`edit-lang-${m.id}`}
                      value={editForm.lang}
                      onChange={(e) => setEditForm({ ...editForm, lang: e.target.value })}
                      className={field}
                    >
                      <option value="Tori">Langue de l'audio : Tori</option>
                      <option value="Goun">Langue de l'audio : Goun</option>
                      <option value="Français">Langue de l'audio : Français</option>
                    </select>
                  ) : (
                    <div>
                      <textarea
                        data-testid={`edit-message-${m.id}`}
                        value={editForm.message}
                        onChange={(e) => setEditForm({ ...editForm, message: e.target.value })}
                        rows={3}
                        maxLength={300}
                        className={`${field} resize-none`}
                      />
                      <p className="mt-1 text-right text-[11px] text-stone-400">
                        {editForm.message.length}/300 caractères
                      </p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      data-testid={`edit-save-${m.id}`}
                      onClick={saveEdit}
                      className="flex items-center gap-1.5 rounded-full bg-[#1C1917] px-4 py-2 text-xs font-semibold text-[#FAF8F5] transition-colors hover:bg-[#C5A059] hover:text-[#1C1917]"
                    >
                      <Check className="h-3.5 w-3.5" /> Enregistrer
                    </button>
                    <button
                      data-testid={`edit-cancel-${m.id}`}
                      onClick={() => setEditing(null)}
                      className="rounded-full border border-stone-300 px-4 py-2 text-xs font-semibold text-stone-500 transition-colors hover:border-stone-500"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="mt-3 text-sm font-semibold text-stone-800">{m.name}</p>
                  {m.relation && (
                    <p className="text-xs text-[#C85A32]">{m.relation}</p>
                  )}
                  {m.kind === "audio" && m.audio_path ? (
                    <div className="mt-3">
                      <AudioPlayer
                        src={`${API}/files/${m.audio_path}`}
                        testId={`admin-audio-${m.id}`}
                      />
                    </div>
                  ) : (
                    <p className="mt-2 text-sm leading-relaxed text-stone-600">
                      {m.message}
                    </p>
                  )}
                </>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                {m.status === "pending" && (
                  <button
                    data-testid={`approve-button-${m.id}`}
                    onClick={() => act(m.id, "approve")}
                    className="flex items-center gap-1.5 rounded-full bg-[#1C1917] px-4 py-2 text-xs font-semibold text-[#FAF8F5] transition-colors hover:bg-[#C5A059] hover:text-[#1C1917]"
                  >
                    <Check className="h-3.5 w-3.5" /> Valider et publier
                  </button>
                )}
                {tab === "approved" && editing !== m.id && (
                  <>
                    <button
                      data-testid={`move-up-${m.id}`}
                      onClick={() => moveTribute(m.id, -1)}
                      disabled={approved.findIndex((x) => x.id === m.id) === 0}
                      className="flex items-center rounded-full border border-stone-300 px-3 py-2 text-xs font-semibold text-stone-500 transition-colors hover:border-[#C5A059] hover:text-[#8a6b2e] disabled:opacity-30"
                      aria-label="Monter"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      data-testid={`move-down-${m.id}`}
                      onClick={() => moveTribute(m.id, 1)}
                      disabled={
                        approved.findIndex((x) => x.id === m.id) === approved.length - 1
                      }
                      className="flex items-center rounded-full border border-stone-300 px-3 py-2 text-xs font-semibold text-stone-500 transition-colors hover:border-[#C5A059] hover:text-[#8a6b2e] disabled:opacity-30"
                      aria-label="Descendre"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
                {editing !== m.id && (
                  <button
                    data-testid={`edit-button-${m.id}`}
                    onClick={() => startEdit(m)}
                    className="flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-xs font-semibold text-stone-500 transition-colors hover:border-[#C5A059] hover:text-[#8a6b2e]"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Modifier
                  </button>
                )}
                <button
                  data-testid={`reject-button-${m.id}`}
                  onClick={() => act(m.id, "reject")}
                  className="flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-xs font-semibold text-stone-500 transition-colors hover:border-[#C85A32] hover:text-[#C85A32]"
                >
                  <X className="h-3.5 w-3.5" /> Refuser
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
